import React, { useState } from "react";
import Sidenav from "../../common/Sidenav";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DriveFolderUploadIcon from "@mui/icons-material/DriveFolderUpload";
import {
  Button,
  InputLabel,
  Typography,
  Grid,
  Card,
  Divider,
  Snackbar,
  Alert,
  useMediaQuery,
} from "@mui/material";

import Header from "../../common/Header";
import NavigatedComponent from "../NavigatedComponent";
import { useLocation, useNavigate } from "react-router-dom";
import { DropzoneArea } from "mui-file-dropzone";
import { apiList, invokeApi } from "../../../services/apiServices";
import { config } from "../../../config/config";
import { useCookies } from "react-cookie";
import IconSidenav from "../../common/IconSidenav";

const AddPhotos = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [cookies] = useCookies();

  const [featuredImage, setFeaturedImage] = useState(null);
  const [status, setStatus] = useState("active");
  const [createdBy] = useState("admin");

  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const isMobileScreen = useMediaQuery("(min-width:360px) and (max-width:500px)");
  const [showSideNav, setShowSideNav] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const toggleSideNav = () => {
    setShowSideNav(!showSideNav);
  };

  const handleFeaturedImage = (files) => {
    if (files && files.length > 0) {
      setFeaturedImage(files[0]);
    } else {
      setFeaturedImage(null);
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setOpenSnackbar(true);
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const uploadFeaturedImage = async () => {
    if (!featuredImage) {
      showSnackbar("Please upload an image first", "warning");
      return "";
    }

    const formData = new FormData();
    formData.append("featured", featuredImage);

    try {
      const response = await fetch("https://api.reef.org.in/addFeaturedPhotoImage", {
        method: "POST",
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        return data.imageUrl || "";
      } else {
        showSnackbar("Failed to upload image", "error");
        return "";
      }
    } catch (error) {
      showSnackbar("Something went wrong. Try again.", "error");
      return "";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return;

    setIsSubmitting(true);

    if (!featuredImage) {
      showSnackbar("Please upload an image", "warning");
      setIsSubmitting(false);
      return;
    }

    const imageUrl = await uploadFeaturedImage();
    if (!imageUrl) {
      setIsSubmitting(false);
      return;
    }

    const params = {
      featuredImage: imageUrl,
      status,
      createdBy,
    };

    try {
      const response = await invokeApi(
        config.getMyCollege + apiList.addPhoto,
        params,
        cookies
      );

      if (response?.status === 200 && response.data.responseCode === "200") {
        showSnackbar("Photo added successfully", "success");
        navigate("/photos-manager/photos-list");
      } else {
        showSnackbar(response?.data?.message || "Failed to add photo", "error");
      }
    } catch (err) {
      showSnackbar("Something went wrong!", "error");
    }

    setIsSubmitting(false);
  };

  return (
    <>
      <Grid sx={{ display: "flex" }}>
        {isMobileScreen ? (
          <IconSidenav />
        ) : showSideNav ? (
          <IconSidenav />
        ) : (
          <Sidenav />
        )}

        <Grid component="main" sx={{ width: "100%", px: 1 }}>
          <Header toggleSideNav={toggleSideNav} />

          <Grid sx={{ py: 1 }}>
            <NavigatedComponent pathname={location.pathname} />

            <Grid sx={{ width: "100%", px: isMobileScreen ? 1 : 5 }}>
              <Card elevation={1} sx={{ my: 5 }}>
                <Grid sx={{ py: 2 }}>
                  <Typography
                    sx={{
                      mb: 2,
                      mx: isMobileScreen ? 2 : 8,
                      fontSize: isMobileScreen ? 14 : 18,
                      fontWeight: 600,
                      textTransform: "uppercase",
                    }}
                  >
                    Add New Photo
                  </Typography>

                  <Divider sx={{ mb: 3 }} />

                  <form onSubmit={handleSubmit}>
                    <Grid container sx={{ width: "100%", justifyContent: "center" }}>
                      <Grid item md={4} xs={11}>
                        <InputLabel sx={{ fontWeight: "bold", my: 1, fontSize: 15 }}>
                          Featured Image *
                        </InputLabel>

                        <DropzoneArea
                          filesLimit={1}
                          onChange={handleFeaturedImage}
                          acceptedFiles={["image/jpeg", "image/png"]}
                          maxFileSize={5000000}
                          showPreviews={true}
                          showPreviewsInDropzone={false}
                          Icon={() => (
                            <CloudUploadIcon sx={{ color: "#1d1a57", width: 35, height: 35 }} />
                          )}
                          dropzoneText={
                            <Typography sx={{ color: "#1d1a57", fontWeight: 600 }}>
                              Upload / Drag image here
                            </Typography>
                          }
                        />
                      </Grid>
                    </Grid>

                    <Divider sx={{ mt: 4 }} />

                    <Grid container sx={{ justifyContent: "center" }}>
                      <Button
                        type="submit"
                        disabled={!featuredImage || isSubmitting}
                        sx={{
                          my: 2,
                          px: 3,
                          background: "#1d1a57",
                          color: "#FFF",
                          "&:hover": { background: "#1d1a57" },
                          "&:disabled": { background: "#ccc" },
                        }}
                      >
                        <DriveFolderUploadIcon /> &nbsp;
                        {isSubmitting ? "Submitting..." : "Publish"}
                      </Button>
                    </Grid>
                  </form>
                </Grid>
              </Card>

              {/* Snackbar */}
              <Snackbar
                open={openSnackbar}
                autoHideDuration={2000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
              >
                <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity}>
                  {snackbarMessage}
                </Alert>
              </Snackbar>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default AddPhotos;
